import React from 'react';

interface ResumeButtonProps {
  onResume: () => void;
  disabled?: boolean;
  isResuming?: boolean; // NEW: Show loading state
  phase: 'awaiting-model-grades' | 'awaiting-ionoff-grade';
  gradesReceived: number;
  gradesRequired: number;
}

/**
 * Resume Button Component
 * 
 * Appears in the timestamp row of:
 * 1. Last model response (when waiting for model grades)
 * 2. Ionoff response (when waiting for ionoff grade)
 * 
 * Enabled only when minimum grades have been assigned.
 */
export const ResumeButton: React.FC<ResumeButtonProps> = ({
  onResume,
  disabled = false,
  isResuming = false,
  phase,
  gradesReceived,
  gradesRequired
}) => {
  const canResume = gradesReceived >= gradesRequired && !disabled && !isResuming;
  
  const getMessage = () => {
    if (isResuming) {
      return phase === 'awaiting-model-grades' 
        ? 'Triggering synthesis...'
        : 'Triggering self-eval...';
    }
    if (phase === 'awaiting-model-grades') {
      return `Grade ${gradesRequired} responses to continue`;
    } else {
      return 'Grade ionoff to continue';
    }
  };
  
  const getProgress = () => {
    return `${gradesReceived}/${gradesRequired}`;
  };
  
  return (
    <div className="inline-flex items-center gap-2">
      <button
        onClick={onResume}
        disabled={!canResume || isResuming}
        className={`
          px-4 py-2 rounded-md text-sm font-medium
          transition-all duration-200
          flex items-center gap-2
          ${canResume && !isResuming
            ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }
        `}
        title={canResume && !isResuming ? 'Resume collabograde flow' : getMessage()}
      >
        {isResuming ? (
          // Loading spinner
          <svg
            className="animate-spin"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" opacity="0.25" />
            <path d="M12 2 A10 10 0 0 1 22 12" opacity="0.75" />
          </svg>
        ) : (
          // Play icon
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        )}
        <span>{isResuming ? 'Resuming...' : 'Resume'}</span>
        {!canResume && !isResuming && (
          <span className="text-xs opacity-75">
            ({getProgress()})
          </span>
        )}
      </button>
      
      {!canResume && !isResuming && (
        <span className="text-xs text-gray-500">
          {getMessage()}
        </span>
      )}
      {isResuming && (
        <span className="text-xs text-gray-500">
          {getMessage()}
        </span>
      )}
    </div>
  );
};

